import { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import "../../styles/modules.css";
import ProductCodes from "./ProductCodes";
import NewProductParametersPanel from "./NewProductParametersPanel";
import PredicateBubbleMap from "./PredicateBubbleMap";
import PredicateCandidatesTable from "./PredicateCandidatesTable";
import CustomerReport from "./CustomerReport";
import { FIND_MATCHING_PRODUCTS_TO_DESCRIPTION, INFER_PRODUCT_CHARACTERISTICS_FROM_DESCRIPTION } from "../../gql/queries/products";

const ResearchSearch = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [selectedProductCode, setSelectedProductCode] = useState(null);
    const [showOptionalFields, setShowOptionalFields] = useState(false);
    const [expectedUser, setExpectedUser] = useState("");
    const [expectedPatient, setExpectedPatient] = useState("");
    const [expectedEnvironment, setExpectedEnvironment] = useState("");
    const [intendedUse, setIntendedUse] = useState("");
    const [rxOtc, setRxOtc] = useState("");
    const [materials, setMaterials] = useState("");
    const [energySource, setEnergySource] = useState("");
    const [showProductCodes, setShowProductCodes] = useState(false);
    const [showPredicateBubbleMap, setShowPredicateBubbleMap] = useState(false);
    const [showPredicateCandidates, setShowPredicateCandidates] = useState(false);
    const [showCustomerReport, setShowCustomerReport] = useState(false);

    // trigger to call backend AI and the query state
    const [searchQueryInferredCharacteristics, { data: inferredData, loading: infLoading, error: infError }] =
        useLazyQuery(INFER_PRODUCT_CHARACTERISTICS_FROM_DESCRIPTION, {
            fetchPolicy: "no-cache",
        });

    const [findMatchingProducts, { data: matchingProductsData, loading: matchingProductsLoading, error: matchingProductsError }] =
        useLazyQuery(FIND_MATCHING_PRODUCTS_TO_DESCRIPTION, {
            fetchPolicy: "no-cache",
        });

    useEffect(() => {
        if (searchQuery.trim() !== "" && showOptionalFields) {

            // call the lazy query with a variables object; pass the search query under the expected variable name
            // (adjust "description" to match the GraphQL variable name in INFER_PRODUCT_CHARACTERISTICS_FROM_DESCRIPTION if different)
            //console.log('calling inference with variable:', { description: searchQuery });
            // show an overlay spinner while the inference call is in progress
            const container = document.getElementById("research-module-container");

            // create spinner overlay if missing
            let spinner = document.getElementById("ai-infer-spinner");
            if (!spinner) {
                spinner = document.createElement("div");
                spinner.id = "ai-infer-spinner";
                Object.assign(spinner.style, {
                    position: "absolute",
                    top: "0",
                    left: "0",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.65)",
                    zIndex: "9999",
                });

                const dot = document.createElement("div");
                Object.assign(dot.style, {
                    width: "44px",
                    height: "44px",
                    border: "6px solid rgba(0,0,0,0.12)",
                    borderTopColor: "var(--ACCELIDEA_DARK_GREEN, #025131)",
                    borderRadius: "50%",
                });
                spinner.appendChild(dot);

                // add keyframes once
                if (!document.getElementById("ai-infer-spinner-style")) {
                    const style = document.createElement("style");
                    style.id = "ai-infer-spinner-style";
                    style.innerHTML = `
                        @keyframes aiInferSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                        #ai-infer-spinner div { animation: aiInferSpin 1s linear infinite; }
                    `;
                    document.head.appendChild(style);
                }

                // ensure container can position absolute children
                if (container) {
                    const computed = window.getComputedStyle(container);
                    if (computed.position === "static" || !container.style.position) {
                        container.style.position = "relative";
                    }
                    container.appendChild(spinner);
                } else {
                    document.body.appendChild(spinner);
                }
            }

            // call the lazy query
            searchQueryInferredCharacteristics({ variables: { description: searchQuery } })
                .then((response) => {
                    const inferred = response?.data?.productCharacteristics?.inferred;
                    if (inferred) {
                        // strip markdown fences if present and parse safely
                        const jsonBlock = inferred.replace(/```json\s*/i, "").replace(/\s*```$/, "");
                        try {
                            const inferredObj = JSON.parse(jsonBlock);
                            setExpectedUser(inferredObj.expectedUser || "");
                            setExpectedPatient(inferredObj.expectedPatient || "");
                            setExpectedEnvironment(inferredObj.expectedEnvironment || "");
                            setIntendedUse(inferredObj.intendedUse || "");
                            setRxOtc(inferredObj.rxOtc || "");
                            setMaterials(inferredObj.materials || "");
                            setEnergySource(inferredObj.energySource || "");
                        } catch (parseErr) {
                            console.error("Failed to parse inferred characteristics JSON:", parseErr, "raw:", jsonBlock);
                        }
                    } else {
                        console.warn("No inferred characteristics returned from AI inference");
                    }
                })
                .catch((err) => {
                    console.error("AI inference error:", err);
                })
                .finally(() => {
                    // remove spinner overlay
                    const s = document.getElementById("ai-infer-spinner");
                    if (s && s.parentNode) s.parentNode.removeChild(s);
                });
        }
    }, [searchQuery, showOptionalFields]);

    const handleProcessDescription = () => {
        if (!searchQuery || !searchQuery.trim()) {
            alert("Please enter a product description to process.");
            return;
        }

        // Placeholder for processing logic
        //console.log("Processing description:", searchQuery);

        // ensure fields become visible, then scroll after DOM updates
        setShowOptionalFields(true);

        // wait for the next paint(s) so new content exists, then scroll to max
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const container = document.getElementById("research-module-container");
                if (container) {
                    const maxScrollLeft = container.scrollWidth - container.clientWidth;
                    container.scrollTo({ left: maxScrollLeft, behavior: "smooth" });
                }
            });
        });
    };

    useEffect(() => {
        if (showProductCodes) {
            // Send searchQuery to backend to search the database for matching products
            //console.log("Searching for products matching characteristics from description:", searchQuery);

            const container = document.getElementById("research-module-container");

            // create spinner overlay if missing
            let spinner = document.getElementById("ai-infer-spinner");
            if (!spinner) {
                spinner = document.createElement("div");
                spinner.id = "ai-infer-spinner";
                Object.assign(spinner.style, {
                    position: "absolute",
                    top: "0",
                    left: "0",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.65)",
                    zIndex: "9999",
                });

                const dot = document.createElement("div");
                Object.assign(dot.style, {
                    width: "44px",
                    height: "44px",
                    border: "6px solid rgba(0,0,0,0.12)",
                    borderTopColor: "var(--ACCELIDEA_DARK_GREEN, #025131)",
                    borderRadius: "50%",
                });
                spinner.appendChild(dot);

                // add keyframes once
                if (!document.getElementById("ai-infer-spinner-style")) {
                    const style = document.createElement("style");
                    style.id = "ai-infer-spinner-style";
                    style.innerHTML = `
                        @keyframes aiInferSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                        #ai-infer-spinner div { animation: aiInferSpin 1s linear infinite; }
                    `;
                    document.head.appendChild(style);
                }

                // ensure container can position absolute children
                if (container) {
                    const computed = window.getComputedStyle(container);
                    if (computed.position === "static" || !container.style.position) {
                        container.style.position = "relative";
                    }
                    container.appendChild(spinner);
                } else {
                    document.body.appendChild(spinner);
                }
            }


            // Validate input before sending a request to avoid server 400 for empty/invalid payloads
            if (!searchQuery || !searchQuery.trim()) {
                console.warn("Skipping product search because searchQuery is empty or whitespace.");
            } else {
                // use an async IIFE so we can await and handle network vs GraphQL errors more clearly
                (async () => {
                    try {
                        //console.log("Searching for products matching characteristics from description:", searchQuery);
                        const response = await findMatchingProducts({ variables: { description: searchQuery } });
                        //console.log("Matching products response:", response);
                        const products = response?.data?.findProductsByDescription || [];
                        //console.log("Found matching products:", products);
                        setSearchResults(products);
                    } catch (err) {
                        // Distinguish network errors (e.g., non-2xx HTTP) from other errors
                        if (err.networkError) {
                            console.error("Network error when finding matching products:", err.networkError);
                        } else if (err.graphQLErrors) {
                            console.error("GraphQL errors when finding matching products:", err.graphQLErrors);
                        } else {
                            console.error("Error finding matching products:", err);
                        }
                    } finally {
                        // remove spinner overlay
                        const s = document.getElementById("ai-infer-spinner");
                        if (s && s.parentNode) s.parentNode.removeChild(s);
                    }

                })();
            }
        }
    }, [showProductCodes, searchQuery]);

    const handleSearch = () => {
        if (!searchQuery || !searchQuery.trim()) {
            alert("Please enter a product description to use in the search.");
            return;
        }

        // Placeholder for search logic
        const searchParams = {
            searchQuery,
            expectedUser,
            expectedPatient,
            expectedEnvironment,
            intendedUse,
            rxOtc,
            materials,
            energySource
        };

        setShowProductCodes(true);

        // wait for the next paint(s) so new content exists, then scroll to max
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const container = document.getElementById("research-module-container");
                if (container) {
                    const maxScrollLeft = container.scrollWidth - container.clientWidth;
                    container.scrollTo({ left: maxScrollLeft, behavior: "smooth" });
                }
            });
        });
    };

    return (
        <div id="research-module-container">
            <div
                style={{
                    display: "flex",
                    gap: 24,
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                }}
            >
                <div
                    className="main-search-panel"
                    style={{ flex: "1 1 420px", minWidth: 320 }} // Makes second search panel come up to the right of the first one
                >
                    <div className="feature-title">
                        <h1>Medical Device Research</h1>
                        <p>
                            Search and analyze FDA-approved medical devices similar to your concept
                        </p>
                    </div>

                    <div className="feature-content">
                        <div>
                            <div className="feature-content-heading">New Product Description</div>
                        </div>

                        {/* container keeps textarea full-width but centers the button below it */}
                        <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <textarea
                                id="product-description"
                                className="product-description"
                                placeholder="Describe your new product and its expected use..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                aria-label="Product description"
                                rows={6}
                                style={{ width: "94%" }}
                                onFocus={(e) =>
                                    (e.currentTarget.style.boxShadow = "0 0 0 4px rgba(59,130,246,0.12)")
                                }
                                onBlur={(e) =>
                                    (e.currentTarget.style.boxShadow = "inset 0 1px 2px rgba(0,0,0,0.03)")
                                }
                            />
                            <button
                                type="submit"
                                className="module-submit-button"
                                onClick={handleProcessDescription}
                                onMouseOver={(e) =>
                                    (e.currentTarget.style.backgroundColor = "var(--ACCELIDEA_MID_GREEN, #038C5A)")
                                }
                                onMouseOut={(e) =>
                                    (e.currentTarget.style.backgroundColor = "var(--ACCELIDEA_DARK_GREEN, #025131)")
                                }
                            >
                                {showOptionalFields ? "Re-process Description" : "Process Description"}
                            </button>
                        </div>
                    </div>
                </div>

                {showOptionalFields && (
                    <>
                    <div className="dotted-left-separator"></div>
                    <div className="new-product-characteristics-panel">
                        <NewProductParametersPanel
                            panelTitle="New Product Technological Characteristics"
                            fields={[
                                {
                                    name: "intendedUse",
                                    label: "Intended Use",
                                    value: intendedUse,
                                    type: "textarea",
                                    placeholder: "Describe the intended use of the device...",
                                    editable: true
                                },
                                {
                                    name: "expectedUser",
                                    label: "Expected User",
                                    value: expectedUser,
                                    editable: true
                                },
                                {
                                    name: "expectedPatient",
                                    label: "Expected Patient",
                                    value: expectedPatient,
                                    editable: true
                                },
                                {
                                    name: "expectedEnvironment",
                                    label: "Expected Environment",
                                    value: expectedEnvironment,
                                    editable: true
                                },
                                {
                                    name: "rxOtc",
                                    label: "Prescription or OTC",
                                    value: rxOtc,
                                    editable: true
                                },
                                {
                                    name: "materials",
                                    label: "Materials",
                                    value: materials,
                                    editable: true
                                },
                                {
                                    name: "energySource",
                                    label: "Energy Source",
                                    value: energySource,
                                    editable: true
                                }
                            ]}
                            addYesMaybeNoButtons={false}
                        />
                        <button
                            type="submit"
                            className="module-submit-button"
                            onClick={handleSearch}
                            onMouseOver={(e) =>
                                (e.currentTarget.style.backgroundColor = "var(--ACCELIDEA_MID_GREEN, #038C5A)")
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.style.backgroundColor = "var(--ACCELIDEA_DARK_GREEN, #025131)")
                            }
                        >
                            Search for Predicates
                        </button>
                    </div>
                    </>
                )}

                {showProductCodes && (
                    <>
                        <div className="dotted-left-separator"></div>
                        <ProductCodes 
                            searchResults={searchResults}
                            setSelectedProductCode={setSelectedProductCode}
                            setShowProductCodes={setShowProductCodes} 
                            setShowPredicateBubbleMap={setShowPredicateBubbleMap} 
                        />
                    </>
                )}

                {showPredicateBubbleMap && (
                    <>
                        <div className="dotted-left-separator"></div>
                        <PredicateBubbleMap 
                            searchResults={searchResults}
                            setSearchResults={setSearchResults}
                            selectedProductCode={selectedProductCode}
                            showPredicateBubbleMap={showPredicateBubbleMap}
                            setShowPredicateBubbleMap={setShowPredicateBubbleMap}
                            setShowProductCodes={setShowProductCodes}
                            setShowPredicateCandidates={setShowPredicateCandidates}
                        />
                    </>
                )}

                {showPredicateCandidates && (
                    <>
                        <div className="dotted-left-separator"></div>
                        <PredicateCandidatesTable 
                            searchResults={searchResults}
                            setSearchResults={setSearchResults}
                            setShowPredicateBubbleMap={setShowPredicateBubbleMap}
                            setShowPredicateCandidates={setShowPredicateCandidates}
                            setShowCustomerReport={setShowCustomerReport}
                        />
                    </>
                )}

                {showCustomerReport && (
                    <>
                        <div className="dotted-left-separator"></div>
                        <CustomerReport 
                            searchResults={searchResults}
                            setShowCustomerReport={setShowCustomerReport}
                        />
                    </>
                )}

            </div>
        </div>
    );
};

export default ResearchSearch;